import { useState, useEffect } from "react";
import { Utensils, FileDown, History, Clock, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";
import {
  InlineText,
  SectionCard as InlineSectionCard,
} from "../../components/cms/InlineEditors";
import { FileUpload } from "../../components/cms/FileUpload";
import { VersionHistoryViewer } from "../../components/cms/VersionHistoryViewer";
import versionHistoryService from "../../services/versionHistoryService";
import { authService } from "../../services/authService";

import cmsApiService, {
  type MenuShowcaseDTO,
} from "../../services/cmsApiService";

export default function NewMenuPageManager() {
  const [historyDialog, setHistoryDialog] = useState<{
    type: string;
    id: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [pdfSection, setPdfSection] = useState<MenuShowcaseDTO>({
    sectionName: "MENU",
    title: "",
    subtitle: "",
    buttonText: "",
    pdfUrl: "",
    pdfFilePath: "",
    images: [],
  });

  // Load PDF section from backend
  useEffect(() => {
    loadPdfSection();
  }, []);

  const loadPdfSection = async () => {
    try {
      setIsLoading(true);
      const response = await cmsApiService.getMenuShowcaseByName("MENU");
      if (response) {
        setPdfSection(response);
      }
    } catch (error: any) {
      if (error?.status !== 404) {
        toast.error("Failed to load menu section");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const savePdfSection = async () => {
    try {
      setIsSaving(true);
      const user = await authService.getProfile();

      if (pdfSection.id) {
        // Update existing section
        await cmsApiService.updateMenuShowcase(pdfSection.id, {
          sectionName: "MENU",
          title: pdfSection.title,
          subtitle: pdfSection.subtitle,
          buttonText: pdfSection.buttonText,
          pdfUrl: pdfSection.pdfUrl,
          pdfFilePath: pdfSection.pdfFilePath,
          images: [],
        });

        versionHistoryService.track({
          entityType: "menu-cards",
          entityId: pdfSection.id,
          action: "update",
          changes: { pdfSection: { old: {}, new: pdfSection } },
          snapshot: pdfSection as unknown as Record<string, unknown>,
          user: user?.name || "Unknown",
        });
      } else {
        // Create new section
        const response = await cmsApiService.addMenuShowcase({
          sectionName: "MENU",
          title: pdfSection.title,
          subtitle: pdfSection.subtitle,
          buttonText: pdfSection.buttonText,
          pdfUrl: pdfSection.pdfUrl || "",
          pdfFilePath: pdfSection.pdfFilePath || "",
          images: [],
        });

        setPdfSection((prev) => ({ ...prev, id: response.id }));

        versionHistoryService.track({
          entityType: "menu-cards",
          entityId: response.id,
          action: "create",
          changes: { pdfSection: { old: {}, new: response } },
          snapshot: response as unknown as Record<string, unknown>,
          user: user?.name || "Unknown",
        });
      }

      toast.success("Menu PDF section saved successfully");
    } catch (error) {
      toast.error("Failed to save menu PDF section");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePdfUpload = async (file: File) => {
    try {
      // Ensure section exists before uploading PDF
      if (pdfSection.id) {
        // Section exists, upload PDF using the dedicated endpoint
        const response = await cmsApiService.updateMenuShowcasePdf(
          pdfSection.id,
          file,
        );
        const pdfUrl = response.pdfUrl || response.url || response;

        // Update local state
        setPdfSection((prev) => ({
          ...prev,
          pdfUrl,
          pdfFilePath: pdfUrl,
        }));

        const user = await authService.getProfile();
        versionHistoryService.track({
          entityType: "menu-cards",
          entityId: pdfSection.id,
          action: "update",
          changes: { pdfUrl: { old: pdfSection.pdfUrl, new: pdfUrl } },
          snapshot: {
            ...pdfSection,
            pdfUrl,
            pdfFilePath: pdfUrl,
          } as unknown as Record<string, unknown>,
          user: user?.name || "Unknown",
        });

        toast.success("PDF uploaded successfully");
        return pdfUrl;
      }

      // Create section first with minimum required data
      const user = await authService.getProfile();
      const response = await cmsApiService.addMenuShowcase({
        sectionName: "MENU",
        title: pdfSection.title || "Menu",
        subtitle: pdfSection.subtitle || "Our Menu",
        buttonText: pdfSection.buttonText || "Download PDF",
        images: [],
      });

      setPdfSection((prev) => ({ ...prev, id: response.id }));

      versionHistoryService.track({
        entityType: "menu-cards",
        entityId: response.id,
        action: "create",
        changes: { pdfSection: { old: {}, new: response } },
        snapshot: response as unknown as Record<string, unknown>,
        user: user?.name || "Unknown",
      });

      // Now upload PDF using the new section ID
      const pdfResponse = await cmsApiService.updateMenuShowcasePdf(
        response.id,
        file,
      );
      const pdfUrl = pdfResponse.pdfUrl || pdfResponse.url || pdfResponse;

      setPdfSection((prev) => ({
        ...prev,
        id: response.id,
        pdfUrl,
        pdfFilePath: pdfUrl,
      }));

      toast.success("Section created and PDF uploaded successfully");
      return pdfUrl;
    } catch (error) {
      toast.error("Failed to upload PDF");
      throw error;
    }
  };

  const handlePdfDelete = async () => {
    if (!pdfSection.id || !pdfSection.pdfUrl) {
      toast.error("No PDF to delete");
      return;
    }

    const performDelete = async () => {
      try {
        setIsDeleting(true);
        const oldPdfUrl = pdfSection.pdfUrl;

        await cmsApiService.deleteMenuShowcasePdf(pdfSection.id!);

        // Update local state to clear PDF
        setPdfSection((prev) => ({
          ...prev,
          pdfUrl: "",
          pdfFilePath: "",
        }));

        const user = await authService.getProfile();
        versionHistoryService.track({
          entityType: "menu-cards",
          entityId: pdfSection.id!,
          action: "update",
          changes: { pdfUrl: { old: oldPdfUrl, new: "" } },
          snapshot: {
            ...pdfSection,
            pdfUrl: "",
            pdfFilePath: "",
          } as unknown as Record<string, unknown>,
          user: user?.name || "Unknown",
        });

        toast.success("PDF deleted successfully");
      } catch (error) {
        toast.error("Failed to delete PDF");
      } finally {
        setIsDeleting(false);
      }
    };

    // Show confirmation toast
    toast(
      (t) => (
        <div className="flex flex-col gap-2">
          <p className="font-medium">
            Are you sure you want to delete this PDF?
          </p>
          <p className="text-sm text-muted-foreground">
            This action cannot be undone.
          </p>
          <div className="flex gap-2 justify-end mt-2">
            <Button
              size="sm"
              variant="blueBg"
              onClick={() => toast.dismiss(t.id)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="red"
              onClick={() => {
                toast.dismiss(t.id);
                performDelete();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
      {
        duration: 10000,
        position: "top-center",
        style: {
          marginTop: "40vh",
        },
      },
    );
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between backdrop-blur-sm bg-white/80 dark:bg-slate-900/80 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 dark:from-white dark:via-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
            <Utensils className="w-8 h-8" />
            Menu Page Manager
          </h1>
        </div>
        <Button variant="outline" onClick={loadPdfSection} disabled={isLoading}>
          <History className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Clock className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p>Loading menu settings...</p>
          </div>
        </div>
      ) : (
        /* Section 1: PDF Download */
        <InlineSectionCard
          title="Section 1: Menu PDF Download"
          description="Downloadable menu PDF with drag & drop upload"
          icon={<FileDown className="w-5 h-5" />}
          onViewHistory={() =>
            pdfSection.id &&
            setHistoryDialog({ type: "menu-cards", id: pdfSection.id })
          }
        >
          <div className="space-y-4">
            <InlineText
              label="Subtitle"
              value={pdfSection.subtitle || ""}
              onChange={(v) => setPdfSection((p) => ({ ...p, subtitle: v }))}
              onSave={savePdfSection}
            />
            <InlineText
              label="Title"
              value={pdfSection.title || ""}
              onChange={(v) => setPdfSection((p) => ({ ...p, title: v }))}
              onSave={savePdfSection}
            />
            <InlineText
              label="Download Button Text"
              value={pdfSection.buttonText || ""}
              onChange={(v) => setPdfSection((p) => ({ ...p, buttonText: v }))}
              onSave={savePdfSection}
            />

            <div className="space-y-2">
              <label htmlFor="menu-pdf-upload" className="text-sm font-medium">
                Menu PDF File
              </label>
              <FileUpload
                value={pdfSection.pdfUrl || ""}
                onChange={(file) => {
                  if (file instanceof File) {
                    handlePdfUpload(file);
                  } else if (typeof file === "string") {
                    setPdfSection((p) => ({
                      ...p,
                      pdfUrl: file,
                      pdfFilePath: file,
                    }));
                  }
                }}
                folder="menu"
                accept=".pdf,application/pdf"
                maxSize={10}
                description="Drag and drop your menu PDF here or click to browse"
                uploadOnSelect={false}
                variant="file"
                showPreview={true}
                showRemoveButton={false}
              />
              {pdfSection.pdfUrl && (
                <div className="mt-2 flex items-center gap-4">
                  <a
                    href={pdfSection.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    View Current PDF
                  </a>
                  <Button
                    variant="red"
                    size="sm"
                    onClick={handlePdfDelete}
                    disabled={isDeleting}
                    className="flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    {isDeleting ? "Deleting..." : "Delete PDF"}
                  </Button>
                </div>
              )}
            </div>

            <Button
              onClick={savePdfSection}
              disabled={isSaving || !pdfSection.title}
              variant="blueBg"
            >
              {(() => {
                if (isSaving) return "Saving...";
                return pdfSection.id ? "Update Section" : "Create Section";
              })()}
            </Button>
          </div>
        </InlineSectionCard>
      )}

      {/* Section 2: Instagram Carousel */}
      {/* <InlineSectionCard
        title="Section 2: Instagram Carousel"
        description="Gallery images with category 'instagram'"
        icon={<Instagram className="w-5 h-5" />}
      >
        <Card className="p-4 bg-muted/50">
          <p className="text-sm text-muted-foreground mb-2">
            ⚠️ Shows Gallery images with category: "instagram"
          </p>
          <Button variant="outline" size="sm" onClick={() => navigateTo('gallery-page')}>
            → Manage Gallery
          </Button>
        </Card>
      </InlineSectionCard> */}

      {historyDialog && (
        <VersionHistoryViewer
          entityType={historyDialog.type}
          entityId={historyDialog.id}
          isOpen={true}
          onClose={() => setHistoryDialog(null)}
        />
      )}
    </div>
  );
}
